/**
 * ActionTracker Component - Track Actions Per Token Per Round
 * 
 * This component manages action tracking for each token during combat:
 * - Tracks actions taken by each token in each round
 * - Integrates with Initiative Tracker for turn-based action logging
 * - Provides action history and current turn action status
 * - Supports different action types (Attack, Move, Spell, Bonus Action, etc.)
 * - @date_of_change 2025-09-06 - Added multiattack mode filtering to only show current component
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { CircleDot, Footprints, MessageSquare, Shield, Sparkles, Swords, Zap } from 'lucide-react';
import ActionEconomyManager, { ACTION_TYPES } from '../../../utils/ActionEconomyManager';
import { getStandardActionRule } from '../../../rules/core/standardActions';
import {
  buildStandardCombatActions
} from '../../../utils/combat/CombatActionCatalog';
import { getCreatureMechanicsData } from '../../../utils/combat/CreatureAbilityCatalog';
import { buildFamiliarActions } from '../../../utils/FamiliarActions';
import {
  requireCombatActionRoute,
  normalizeCombatActionRequest
} from '../../../utils/combat/CombatActionRequestNormalizer';
import {
  selectCombatantProjection,
  selectCombatantActions,
  selectCombatantCombatStats
} from '../../../utils/battleRoom/BattleRoomCombatantSelectors';
import { getCombatantActionBlockReason } from '../../../utils/combat/CombatantNormalizer';
import './ActionTracker.css';
import './CreatureAbilityStates.css';

const buildActionListKey = (action) => {
  if (action?.id) return action.id;
  const normalizedName = String(action?.name || '').trim().toLowerCase();
  const activationType = String(action?.uiType || action?.actionType || action?.activationType || action?.type || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ');
  return `${activationType}|${normalizedName}`;
};

const dedupeActionEntries = (actions) => {
  const seen = new Set();
  return (Array.isArray(actions) ? actions : []).filter((action) => {
    const key = buildActionListKey(action);
    if (!key) return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const SEARCH_SENSE_OPTIONS = [
  { value: 'general', label: 'General awareness' },
  { value: 'sight', label: 'Sight' },
  { value: 'hearing', label: 'Hearing' },
  { value: 'smell', label: 'Smell' }
];

const buildKeenSenseEvidence = (combatant) => {
  const data = getCreatureMechanicsData(combatant);
  const traits = (Array.isArray(data?.traits) ? data.traits : []).filter((trait) => (
    trait?.mechanics?.kind === 'checkAdvantage'
    && trait.mechanics.ability === 'wisdom'
    && trait.mechanics.skill === 'perception'
  ));
  const senses = [...new Set(traits.flatMap((trait) => trait.mechanics.senses || []))];
  const passivePerception = Number(data?.senses?.passivePerception);
  return {
    traits,
    senses,
    passivePerception: Number.isFinite(passivePerception) ? passivePerception : null
  };
};

const isGenericAttackPlaceholder = (action) => {
  const name = String(action?.name || '').trim().toLowerCase();
  const genericNames = new Set(['attack', 'weapon attack', 'melee weapon attack', 'ranged weapon attack']);
  if (!genericNames.has(name) || action?.isMonsterAction) return false;
  const sourceNames = [
    action?.weaponData?.name,
    action?.weaponData?.label,
    action?.originalWeaponName,
    action?.originalAction?.name,
    action?.originalAction?.label,
  ].map((value) => String(value || '').trim().toLowerCase()).filter(Boolean);
  const hasConcreteSourceName = sourceNames.some((value) => !genericNames.has(value));
  return (
    !hasConcreteSourceName
    && (
      name !== 'attack'
      || (
        String(action?.type || '').trim().toLowerCase() === 'attack'
        && !action?.attack
        && !action?.originalAction
      )
    )
  );
};

const LONG_ACTION_TRAY_THRESHOLD = 7;

const ACTION_SUBGROUPS = {
  weapons: { label: 'Weapons' },
  spells: { label: 'Spells' },
  familiar: { label: 'Familiar' },
  standard: { label: 'Standard Actions' },
  movement: { label: 'Movement' },
  objects: { label: 'Objects & Free' },
  other: { label: 'Other' }
};

const ACTION_SUBGROUP_ORDER = ['weapons', 'spells', 'familiar', 'standard', 'movement', 'objects', 'other'];

const resolveActionSubgroup = (action) => {
  const type = String(action?.type || '').trim().toLowerCase();
  const name = String(action?.name || action?.displayName || '').trim().toLowerCase();
  const activation = String(action?.activationType || action?.actionType || action?.uiType || '').trim().toLowerCase();

  if (action?.familiarAction || action?.familiarDelivery || name.includes('familiar')) return 'familiar';
  if (type === 'spell' || action?.spellLevel != null || action?.school) return 'spells';
  if (type === 'weapon' || type === 'attack' || action?.attack || action?.weaponData) return 'weapons';
  if (type === 'move' || activation.includes('movement') || name.startsWith('move ')) return 'movement';
  if (type === 'free' || type === 'object' || activation.includes('free') || activation.includes('object')) return 'objects';
  if (action?.standardActionKey || action?.ruleReference) return 'standard';
  return 'other';
};

const groupActionItems = (items) => {
  const buckets = new Map(ACTION_SUBGROUP_ORDER.map((key) => [key, []]));
  (Array.isArray(items) ? items : []).forEach((item) => {
    const key = resolveActionSubgroup(item);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(item);
  });

  return ACTION_SUBGROUP_ORDER
    .map((key) => ({
      key,
      label: ACTION_SUBGROUPS[key]?.label || 'Other',
      items: buckets.get(key) || []
    }))
    .filter((group) => group.items.length > 0);
};

const buildConditionalStandardAction = (ruleKey) => {
  const rule = getStandardActionRule(ruleKey);
  if (!rule) return null;

  return {
    standardActionKey: rule.key,
    type: rule.actionType,
    name: rule.name,
    description: rule.description,
    uiType: rule.actionType,
    activationType: rule.activationType,
    actionType: rule.activationType,
    executionRoute: rule.executionRoute,
    targetRequirement: rule.targetRequirement,
    ruleReference: rule.ruleReference,
    effectTags: Array.isArray(rule.effectTags) ? rule.effectTags.slice() : []
  };
};

const collectPropertyLabels = (...sources) => sources.flatMap((source) => {
  if (Array.isArray(source)) return source;
  if (typeof source === 'string') return source.split(/[,|]/);
  return [];
}).map((property) => String(property || '').trim().toLowerCase()).filter(Boolean);

const LIGHT_MELEE_WEAPON_NAME_PATTERN = /\b(club|dagger|handaxe|light hammer|sickle|scimitar|shortsword)\b/i;

const hasExplicitOffHandFlag = (combatant, action) => Boolean(
  combatant?.equipment?.offhand
  || combatant?.equipment?.offHand
  || combatant?.equipment?.dualWield
  || combatant?.equipment?.twoWeaponFighting
  || action?.offHand
  || action?.isOffHand
  || action?.weaponData?.offHand
  || action?.weaponData?.offhand
  || action?.originalAction?.offHand
  || action?.originalAction?.offhand
);

const isWeaponAttackAction = (action) => {
  const type = String(action?.type || '').trim().toLowerCase();
  return type === 'weapon' || type === 'attack' || Boolean(action?.attack || action?.weaponData);
};

const isLightMeleeWeaponAction = (action) => {
  if (!isWeaponAttackAction(action)) return false;

  const name = String(action?.name || action?.weaponData?.name || action?.originalAction?.name || '').toLowerCase();
  const properties = collectPropertyLabels(
    action?.properties,
    action?.weaponData?.properties,
    action?.originalAction?.properties,
    action?.originalAction?.weaponData?.properties
  );
  const attackType = String(action?.attack?.type || action?.attackType || action?.weaponData?.attackType || '').toLowerCase();
  const hasLightProperty = properties.some((property) => property.includes('light')) || LIGHT_MELEE_WEAPON_NAME_PATTERN.test(name);
  const hasMeleeSignal = attackType.includes('melee')
    || LIGHT_MELEE_WEAPON_NAME_PATTERN.test(name)
    || String(action?.range || '').toLowerCase().includes('5');

  return hasLightProperty && hasMeleeSignal;
};

const buildOffHandAttackAction = (combatant, actions) => {
  const weaponActions = Array.isArray(actions) ? actions.filter(isWeaponAttackAction) : [];
  if (weaponActions.length === 0) return null;

  const explicitOffHand = weaponActions.find((action) => hasExplicitOffHandFlag(combatant, action));
  const lightMeleeWeapons = weaponActions.filter(isLightMeleeWeaponAction);

  // D&D 5e two-weapon fighting needs a real weapon payload; a generic bonus label cannot
  // resolve hit, range, or damage. date_of_change: 2026-06-23
  if (!explicitOffHand && lightMeleeWeapons.length < 2) {
    return null;
  }

  const sourceAction = explicitOffHand || lightMeleeWeapons[1] || lightMeleeWeapons[0];
  if (!sourceAction) return null;

  const weaponName = sourceAction.name || sourceAction.weaponData?.name || sourceAction.originalAction?.name || 'weapon';
  return {
    ...sourceAction,
    type: 'attack',
    uiType: 'bonus',
    activationType: 'bonus action',
    actionType: 'bonus action',
    name: `Off-hand Attack (${weaponName})`,
    description: sourceAction.description || sourceAction.desc || `Bonus action attack with ${weaponName}.`,
    desc: sourceAction.desc || sourceAction.description || `Bonus action attack with ${weaponName}.`,
    offHand: true,
    originalWeaponName: weaponName,
    originalAction: sourceAction.originalAction || sourceAction
  };
};

const getOffHandPrerequisite = (combatantId, offHandAction, availableActions) => {
  if (!offHandAction?.offHand || !combatantId) {
    return { met: true, reason: '' };
  }

  const turnActions = ActionEconomyManager.getTurnActions(combatantId) || [];
  const lightWeaponNames = new Set(
    (Array.isArray(availableActions) ? availableActions : [])
      .filter(isLightMeleeWeaponAction)
      .map((action) => String(action?.name || '').trim().toLowerCase())
      .filter(Boolean)
  );
  const usedLightWeaponAttack = turnActions.some((entry) => (
    entry?.type === ACTION_TYPES.ACTION
    && lightWeaponNames.has(String(entry?.name || '').trim().toLowerCase())
  ));

  return usedLightWeaponAttack
    ? { met: true, reason: '' }
    : { met: false, reason: 'Attack with a Light melee weapon first.' };
};

const ActionTracker = ({ 
  currentCombatant, 
  activeCombatant = null,
  round, 
  combatStarted, 
  onActionTaken,
  onFamiliarAction = null,
  allTokens = [],
  onDiceRoll,
  // Integration props (optional): selected token id + per-token action usage flags
  selectedTokenId,
  actionsUsed,
  // @date_of_change 2025-09-07 - Targeting props removed, handled by MultiattackTargetingPopup
  // Multiattack mode props (kept for filtering actions)
  multiattackSequence,
  isInMultiattackMode,
  // @date_of_change: 2025-10-09 - Role-based UI gating for GM-only controls
  isGameMaster = false,
  // @date_of_change: 2025-10-11 - Token ownership control for players
  canControlToken = null,
  // date_of_change: 2026-06-07
  // WHAT: Lets the action panel reveal the right-side character sheet instead of duplicating resources.
  // WHY: Spell slots and weapon details live on the sheet; opening that panel keeps one source of UI truth.
  onOpenDetailsPanel = null,
  gmOverride = null,
  onGmOverrideChange = null,
  onEditCombatLog = null
}) => {
  // Action history: { round: number, tokenId: string, actions: Array }
  const [actionHistory, setActionHistory] = useState({});
  
  // Current turn actions for the active combatant
  const [currentTurnActions, setCurrentTurnActions] = useState([]);

  // @date_of_change 2025-08-30
  // Action economy state for current combatant
  const [actionSummary, setActionSummary] = useState(null);
  // @date_of_change 2025-11-04 - Vertical stack trays: which category is expanded
  const [expandedKey, setExpandedKey] = useState(null);
  const [searchDeclaration, setSearchDeclaration] = useState(null);
  
  const gmOverrideMode = Boolean(isGameMaster && gmOverride?.enabled);
  const gmOverrideNote = String(gmOverride?.note || '');


  // @date_of_change: 2025-09-21 - Enhanced speed detection for different token types
  const getTokenSpeed = useCallback((token) => {
    if (!token) return 30;

    // Handle different speed data formats for characters vs monsters
    if (typeof token.speed === 'number') {
      return token.speed;
    } else if (typeof token.speed === 'object' && token.speed?.walk) {
      return token.speed.walk;
    } else if (token.characterData?.speed) {
      // Character data speed
      return typeof token.characterData.speed === 'number'
        ? token.characterData.speed
        : token.characterData.speed?.walk || 30;
    } else if (token.monsterData?.speed) {
      // Monster data speed
      return typeof token.monsterData.speed === 'number'
        ? token.monsterData.speed
        : token.monsterData.speed?.walk || 30;
    } else if (token.canonicalData?.speed) {
      // Canonical data speed
      return typeof token.canonicalData.speed === 'number'
        ? token.canonicalData.speed
        : token.canonicalData.speed?.walk || 30;
    }

    // Default human walking speed
    return 30;
  }, []);

  // @date_of_change 2025-11-09 - Lint cleanup: removed unused consumeActionEconomy alias

  // Reset current turn actions when combatant changes
  useEffect(() => {
    if (!currentCombatant?.id) {
      setCurrentTurnActions([]);
      return;
    }

    const roundKey = `${round}-${currentCombatant.id}`;
    setCurrentTurnActions(actionHistory[roundKey] || []);
  }, [actionHistory, currentCombatant, round]);

  useEffect(() => {
    if (!currentCombatant?.id) {
      setActionSummary(null);
      setExpandedKey(null);
      return;
    }

    // The display panel may follow GM token inspection; only InitiativeTracker/BattleRoom may
    // start turns. Calling startTurn here refunded movement/actions on selection. date_of_change: 2026-06-23
    const speed = getTokenSpeed(currentCombatant);
    const summary = ActionEconomyManager.synchronizeCombatFlags(
      currentCombatant.id,
      currentCombatant.combatFlags || {},
      speed
    );
    setActionSummary(summary);
  }, [currentCombatant, round, getTokenSpeed]);

  // Handle round changes to reset action economy
  useEffect(() => {
    ActionEconomyManager.startRound(round);
  }, [round]);

  // Action types and their properties
  const actionTypes = {
    attack: { name: 'Attack', Icon: Swords },
    move: { name: 'Move', Icon: Footprints },
    spell: { name: 'Spell', Icon: Sparkles },
    bonus: { name: 'Bonus Action', Icon: Zap },
    reaction: { name: 'Reaction', Icon: Shield },
    free: { name: 'Free Action', Icon: MessageSquare },
    // Generic main action bucket (Dash, Disengage, Dodge, Help, Hide, Ready, Search, Use an Object)
    action: { name: 'Action', Icon: CircleDot }
  };

  // @date_of_change 2025-11-09 - Lint cleanup: removed unused handleAttackAction (attack routing handled in parent)

  const combatantCombatStats = useMemo(
    () => selectCombatantCombatStats(currentCombatant),
    [currentCombatant]
  );
  const projectedCombatant = useMemo(
    () => (currentCombatant ? selectCombatantProjection(currentCombatant) : null),
    [currentCombatant]
  );
  const actionBlockReason = useMemo(
    () => getCombatantActionBlockReason(projectedCombatant || currentCombatant, currentCombatant),
    [projectedCombatant, currentCombatant]
  );
  const tokenActions = useMemo(
    () => (currentCombatant ? selectCombatantActions(currentCombatant) : []),
    [currentCombatant]
  );
  const keenSenseEvidence = useMemo(
    () => buildKeenSenseEvidence(currentCombatant),
    [currentCombatant]
  );
  const familiarActions = useMemo(
    () => buildFamiliarActions(currentCombatant, allTokens, tokenActions),
    [currentCombatant, allTokens, tokenActions]
  );
  const standardActions = useMemo(
    () => buildStandardCombatActions(combatantCombatStats?.speed ?? getTokenSpeed(currentCombatant)),
    [combatantCombatStats, currentCombatant, getTokenSpeed]
  );
  

  // @date_of_change 2025-11-09 - Lint cleanup: removed unused requiresTarget helper

  const collectCombatantFeatures = (combatant) => {
    const sources = [
      combatant?.features,
      combatant?.classFeatures,
      combatant?.abilities,
      combatant?.entityPayload?.features,
      combatant?.entityPayload?.classFeatures,
      combatant?.characterData?.features,
      combatant?.characterData?.classFeatures,
      combatant?.canonicalData?.features,
      combatant?.canonicalData?.classFeatures,
      combatant?.data?.features,
      combatant?.data?.classFeatures
    ];

    return sources.flatMap((source) => {
      if (!source) return [];
      if (Array.isArray(source)) return source;
      if (typeof source === 'object') return Object.values(source).flat();
      return [source];
    }).filter(Boolean);
  };

  const collectCombatantClasses = (combatant) => {
    const sources = [
      combatant?.classes,
      combatant?.class,
      combatant?.characterClass,
      combatant?.entityPayload?.classes,
      combatant?.entityPayload?.class,
      combatant?.characterData?.classes,
      combatant?.characterData?.class,
      combatant?.canonicalData?.classes,
      combatant?.canonicalData?.class,
      combatant?.data?.classes,
      combatant?.data?.class
    ];

    return sources.flatMap((source) => {
      if (!source) return [];
      if (Array.isArray(source)) return source;
      return [source];
    }).filter(Boolean);
  };

  const hasCunningAction = (combatant) => {
    if (!combatant) return false;

    const features = collectCombatantFeatures(combatant);
    const hasFeature = features.some(feature => {
      const name = String(feature?.name || feature?.title || feature || '').toLowerCase();
      const desc = String(feature?.desc || feature?.description || feature?.text || '').toLowerCase();
      return name.includes('cunning action')
        || (
          desc.includes('bonus action')
          && desc.includes('dash')
          && desc.includes('disengage')
          && desc.includes('hide')
        );
    });
    if (hasFeature) return true;

    const classes = collectCombatantClasses(combatant);
    return classes.some(cls => {
      const className = String(cls?.name || cls?.className || cls || '').toLowerCase();
      return className.includes('rogue');
    });
  };

  // Check if creature can Dash as a bonus action without granting full Cunning Action.
  const canCreatureBonusDash = (combatant) => {
    if (hasCunningAction(combatant)) return true;

    return collectCombatantFeatures(combatant).some(feature => {
      const name = String(feature?.name || feature?.title || feature || '').toLowerCase();
      const desc = String(feature?.desc || feature?.description || feature?.text || '').toLowerCase();
      return name.includes('adrenaline rush')
        || desc.includes('dash as a bonus action')
        || desc.includes('take the dash action as a bonus action')
        || desc.includes('bonus action: dash');
    });
  };

  // Fighters only (Second Wind)
  const isFighter = (combatant) => {
    try {
      const classes = Array.isArray(combatant?.classes) ? combatant.classes : (combatant?.classes ? [combatant.classes] : []);
      if (classes.length) {
        return classes.some(c => String(c.name || c.className || '').toLowerCase().includes('fighter'));
      }
      // Some tokens may expose class features directly
      const feats = Array.isArray(combatant?.features) ? combatant.features : (combatant?.features ? [combatant.features] : []);
      return feats.some(f => String(f.name || f.title || '').toLowerCase().includes('second wind'));
    } catch (_) { return false; }
  };

  const canTwoWeaponFight = (combatant, actions) => {
    try { return Boolean(buildOffHandAttackAction(combatant, actions)); } catch (_) { return false; }
  };

  // Hide actions that should not be clickable in UI (engine-driven effects)
  // Specifically: Sneak Attack must be applied automatically, not chosen as an action
  const shouldHideActionForUI = (action) => {
    try {
      const name = String(action?.name || action?.title || '').toLowerCase();
      if (!name) return false;
      if (name.includes('sneak attack')) return true;
      if (name === 'cunning action') return true;
    } catch (_) {}
    return false;
  };

  // Map ActionEconomyManager action types to local UI action categories
  const mapEconomyTypeToUiType = (economyType, actionName) => {
    switch (economyType) {
      case ACTION_TYPES.MOVEMENT:
        return 'move';
      case ACTION_TYPES.BONUS_ACTION:
        return 'bonus';
      case ACTION_TYPES.REACTION:
        return 'reaction';
      case ACTION_TYPES.FREE_ACTION:
      case ACTION_TYPES.FREE_OBJECT:
        return 'free';
      case ACTION_TYPES.ACTION:
      default: {
        // Heuristic: treat common attack names as 'attack', otherwise generic 'action'
        const attackNames = new Set(['Attack', 'Longsword', 'Shortbow', 'Bite', 'Claw']);
        return attackNames.has(actionName) ? 'attack' : 'action';
      }
    }
  };

  const resolveUiTypeForAction = (action) => {
    const activationLabel = String(action?.actionType || action?.activationType || '')
      .trim()
      .toLowerCase()
      .replace(/_/g, ' ');
    if (activationLabel.includes('bonus')) return 'bonus';
    if (activationLabel.includes('reaction')) return 'reaction';
    if (activationLabel.includes('free')) return 'free';

    const explicitType = String(action?.type || '').trim().toLowerCase();
    if (explicitType === 'bonus') return 'bonus';
    if (explicitType === 'reaction') return 'reaction';
    if (explicitType === 'move') return 'move';
    if (explicitType === 'free' || explicitType === 'object') return 'free';
    if (explicitType === 'spell' || explicitType === 'weapon' || action?.attack || action?.isMonsterAction) {
      return 'attack';
    }

    const name = typeof action?.name === 'string'
      ? action.name
      : (action?.name?.name || action?.name?.toString() || 'Unknown Action');
    const econType = ActionEconomyManager.getActionType(name);
    return mapEconomyTypeToUiType(econType, name);
  };

  const resolveActionEconomyTypeForAction = (action, fallbackCategory = null) => {
    const activationLabel = String(action?.activationType || action?.actionType || '')
      .trim()
      .toLowerCase()
      .replace(/_/g, ' ');

    if (activationLabel.includes('bonus') || action?.uiType === 'bonus' || action?.type === 'bonus') {
      return ACTION_TYPES.BONUS_ACTION;
    }
    if (activationLabel.includes('reaction') || action?.uiType === 'reaction' || action?.type === 'reaction') {
      return ACTION_TYPES.REACTION;
    }
    if (activationLabel.includes('movement') || action?.uiType === 'movement' || action?.uiType === 'move' || action?.type === 'move') {
      return ACTION_TYPES.MOVEMENT;
    }
    if (activationLabel.includes('free object') || action?.uiType === 'object' || action?.type === 'object') {
      return ACTION_TYPES.FREE_OBJECT;
    }
    if (activationLabel.includes('free') || action?.uiType === 'free' || action?.type === 'free') {
      return ACTION_TYPES.FREE_ACTION;
    }
    if (activationLabel === 'action') return ACTION_TYPES.ACTION;
    if (fallbackCategory === 'bonus') return ACTION_TYPES.BONUS_ACTION;

    const name = typeof action?.name === 'string'
      ? action.name
      : (action?.name?.name || action?.name?.toString() || 'Unknown Action');
    return ActionEconomyManager.getActionType(name);
  };

  // Subscribe to ActionEconomyManager changes so external updates trigger UI refresh
  useEffect(() => {
    if (!currentCombatant?.id) return;
    // @date_of_change: 2025-10-04 - Removed spammy subscription logging
    const unsubscribe = ActionEconomyManager.onChange((evt) => {
      console.log(`[ActionTracker] Received ActionEconomy event:`, evt);
      // Only refresh if event affects current combatant or is a global event
      const isGlobal = evt?.type === 'roundStart' || evt?.type === 'reset' || evt?.type === 'import';
      const affectsCurrent = evt?.combatantId && currentCombatant && evt.combatantId === currentCombatant.id;
      console.log(`[ActionTracker] Event relevance - isGlobal: ${isGlobal}, affectsCurrent: ${affectsCurrent}`);
      if (!isGlobal && !affectsCurrent) return;

      const speed = getTokenSpeed(currentCombatant);
      const summary = ActionEconomyManager.getActionSummary(currentCombatant.id, speed);
      console.log(`[ActionTracker] Updating action summary:`, summary);
      setActionSummary(summary);

      // Reset/import events replace the combat snapshot. Local presentation
      // history must not survive into the newly loaded or reset room.
      // date_of_change: 2026-08-20
      if (evt?.type === 'reset' || evt?.type === 'import') {
        setActionHistory({});
        setCurrentTurnActions([]);
      }

      // Optionally mirror basic action logs for external changes into local history
      if (evt?.type === 'actionUsed' && affectsCurrent) {
        const roundKey = `${round}-${currentCombatant.id}`;
        const actionItem = {
          id: Date.now(),
          type: mapEconomyTypeToUiType(evt.actionType, evt.actionName),
          name: evt.actionName,
          timestamp: new Date().toLocaleTimeString(),
          details: { movementCost: evt.movementCost || 0 }
        };
        setActionHistory(prev => ({
          ...prev,
          [roundKey]: [ ...(prev[roundKey] || []), actionItem ]
        }));
        setCurrentTurnActions(prev => [ ...prev, actionItem ]);
      }

      if (evt?.type === 'turnStart' && affectsCurrent) {
        // Clear current-turn local list at the start of the turn for this combatant
        setCurrentTurnActions([]);
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [currentCombatant, round, getTokenSpeed]);

  // Handle action button clicked
  // @date_of_change 2025-09-07 - Simplified to handle only action economy tracking, targeting handled by MultiattackTargetingPopup
  const handleActionClicked = async (action) => {
    const isViewingOutOfTurn = Boolean(
      combatStarted
      && activeCombatant?.id
      && currentCombatant?.id
      && activeCombatant.id !== currentCombatant.id
    );
    const actingCombatant = isViewingOutOfTurn && !gmOverrideMode
      ? activeCombatant
      : currentCombatant;
    const actingProjection = actingCombatant?.id === currentCombatant?.id
      ? (projectedCombatant || currentCombatant)
      : (selectCombatantProjection(actingCombatant) || actingCombatant);

    console.log('[ACTION_TRACKER:CLICKED]', {
      actionName: action.name,
      actionType: action.type,
      hasOnActionTaken: typeof onActionTaken === 'function',
      selectedTokenId,
      currentCombatantId: currentCombatant?.id
    });

    if (!currentCombatant?.id) {
      console.error('[ActionTracker] No current combatant');
      return;
    }

    if (actionBlockReason) {
      console.warn('[ActionTracker] Action blocked:', actionBlockReason);
      return;
    }

    if (isViewingOutOfTurn && !gmOverrideMode) {
      console.warn('[ActionTracker] Action blocked while inspecting out-of-turn combatant', {
        viewingCombatantId: currentCombatant.id,
        activeCombatantId: activeCombatant.id,
        actionName: action.name
      });
      return;
    }

    if (action?.familiarAction && action.familiarAction !== 'deliver_touch_spell') {
      if (typeof onFamiliarAction === 'function') {
        await onFamiliarAction({ actor: actingProjection, action });
      }
      return;
    }

    if (typeof onOpenDetailsPanel === 'function') {
      const routeForPanel = requireCombatActionRoute(action);
      const preferredTab = routeForPanel === 'spell'
        ? 'spells'
        : (routeForPanel === 'attack' ? 'equipment' : 'actions');
      onOpenDetailsPanel(preferredTab);
    }

    // Selection is informational during combat; execution belongs to the rendered actor only,
    // never a target token that happened to be clicked. date_of_change: 2026-06-23
    const attackerId = actingCombatant?.id || currentCombatant.id;
    const attacker = actingProjection;
    const normalizedRequest = normalizeCombatActionRequest({
      attackerId,
      attacker,
      actionData: action,
      targetIds: [],
      gmOverride: gmOverrideMode
        ? {
            enabled: true,
            note: gmOverrideNote.trim() || 'GM action-economy override'
          }
        : null
    });
    // BattleRoom owns every combat mutation. Executing self-buffs or utility
    // actions here created a second action-economy state: the singleton and log
    // advanced, while token combatFlags (the targeting engine's source) did not.
    // Familiar-only commands return above through their explicit canonical
    // handler; every other action follows this single request path.
    // date_of_change: 2026-07-15
    if (onActionTaken && attackerId) {
      console.log('[ACTION_TRACKER:DELEGATE_TO_BATTLEROOM]', {
        attackerId,
        actionName: action.name,
        route: normalizedRequest.route,
        callingOnActionTaken: true
      });
      return onActionTaken(normalizedRequest);
    }

    console.error('[ActionTracker] Cannot execute a combat action without the BattleRoom action owner.', {
      attackerId,
      actionName: action.name,
      route: normalizedRequest.route
    });
    return false;
  };

  const handleActionButtonClicked = async (action) => {
    if (action?.standardActionKey === 'search') {
      setSearchDeclaration({
        action,
        actionKey: buildActionListKey(action),
        skill: '',
        sense: ''
      });
      return;
    }
    await handleActionClicked(action);
  };

  const executeDeclaredSearch = async () => {
    if (!searchDeclaration?.action) return false;
    const skill = searchDeclaration.skill;
    const sense = searchDeclaration.sense;
    if (!['perception', 'investigation'].includes(skill)) return false;
    if (skill === 'perception' && !SEARCH_SENSE_OPTIONS.some((option) => option.value === sense)) return false;

    const result = await handleActionClicked({
      ...searchDeclaration.action,
      searchSkill: skill,
      ...(skill === 'perception' ? { searchSense: sense } : {})
    });
    if (result !== false) setSearchDeclaration(null);
    return result;
  };

  // @date_of_change: 2025-10-11 - Check if current user can control the current combatant token
  const canControlCurrentCombatant = useMemo(() => {
    if (!currentCombatant?.id) return false;
    if (isGameMaster) return true; // GM can always control any token
    if (typeof canControlToken === 'function') {
      try {
        return !!canControlToken(currentCombatant.id);
      } catch (error) {
        console.warn('[ActionTracker] Error checking token control:', error);
        return false;
      }
    }
    return false; // Default to no control if canControlToken not provided
  }, [currentCombatant, isGameMaster, canControlToken]);

  if (!currentCombatant?.id) {
    return (
      <div className="action-tracker">
        <div className="no-combatant">
          <p>No token selected</p>
          <p>Select a token to see available actions</p>
        </div>
      </div>
    );
  }

  // Show message if user cannot control the current combatant
  if (!canControlCurrentCombatant) {
    return (
      <div className="action-tracker">
        <div className="no-combatant">
          <p>Actions not available</p>
          <p>You do not control {currentCombatant.name}</p>
          <small>Contact your GM to assign control of this token.</small>
        </div>
      </div>
    );
  }

  if (actionBlockReason) {
    return (
      <div className="action-tracker">
        <div className="no-combatant">
          <p>Actions unavailable</p>
          <p>{actionBlockReason}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="action-tracker">
      {/* @date_of_change 2025-09-07 - Targeting UI removed, handled by MultiattackTargetingPopup */}
      
      {!currentCombatant ? (
        <div className="no-combatant">
          <p>No token selected</p>
          <p>Select a token to see available actions</p>
        </div>
      ) : (
        <>
          <div className="action-header">
            <h3>
              {currentCombatant.name || 'Unknown'} Actions
              {currentCombatant.type === 'monster' && (
                <span className="monster-tag"> (Monster)</span>
              )}
            </h3>
            {combatStarted && activeCombatant?.id && activeCombatant.id !== currentCombatant.id && (
              <div className="turn-preview-notice">
                Viewing {currentCombatant.name || 'this token'}. Current turn: {activeCombatant.name || 'active combatant'}.
              </div>
            )}
          </div>

          {/* The BattleRoom owns this state because mobile drawers unmount.
              Execution receives the same audited override contract as this UI.
              date_of_change: 2026-07-27 */}
          {isGameMaster && (
            <div className="gm-override-controls">
              <div className="gm-override-toggle">
                <label>
                  <input
                    type="checkbox"
                    checked={gmOverrideMode}
                    onChange={(event) => onGmOverrideChange?.({
                      enabled: event.target.checked,
                      note: gmOverrideNote
                    })}
                  />
                  Ignore action economy
                </label>
                <button
                  className="gm-log-edit-button"
                  type="button"
                  onClick={onEditCombatLog}
                  title="Open the combat log editor"
                >
                  Edit Log
                </button>
              </div>
              {gmOverrideMode && (
                <div className="gm-override-panel">
                  <label className="gm-override-note">
                    <span>Audit note</span>
                    <input
                      type="text"
                      placeholder="Why is this action allowed?"
                      value={gmOverrideNote}
                      onChange={(event) => onGmOverrideChange?.({
                        enabled: true,
                        note: event.target.value
                      })}
                    />
                  </label>
                </div>
              )}
            </div>
          )}

          {/* date_of_change: 2026-06-07
              WHAT: The action panel now stays focused on actions only.
              WHY: Spell slots are canonical character-sheet resources, and duplicating them here
              created split-brain UI when combat spell casts updated the session snapshot. */}

          {/* Vertical Action Economy Stack with Expandable Trays */}
          {actionSummary && (
            <div className="vertical-economy">
              {(() => {
                const speed = getTokenSpeed(currentCombatant);
                const withdrawFeet = Number(currentCombatant?.combatFlags?.cunningStrikeWithdraw?.maxFeet);
                const categories = [
                  { key: 'action', label: 'Action', icon: 'ACT', badge: actionSummary.action.available ? 'Available' : 'Used', status: actionSummary.action.available ? 'available' : 'used' },
                  { key: 'bonus', label: 'Bonus Action', icon: 'BON', badge: actionSummary.bonus_action.available ? 'Available' : 'Used', status: actionSummary.bonus_action.available ? 'available' : 'used' },
                  // Token dragging is the only movement command for now. Keeping
                  // this row informational avoids a second "Move N ft" action
                  // that can spend movement without selecting a destination.
                  // date_of_change: 2026-07-18
                  {
                    key: 'move',
                    label: 'Movement',
                    icon: 'MOV',
                    badge: Number.isFinite(withdrawFeet)
                      ? `Withdraw ${withdrawFeet} ft`
                      : `${Math.max(0, actionSummary.movement.available)} ft left`,
                    status: 'neutral',
                    interactive: false
                  },
                  { key: 'reaction', label: 'Reaction', icon: 'REA', badge: actionSummary.reaction.available ? 'Available' : 'Used', status: actionSummary.reaction.available ? 'available' : 'used' },
                  { key: 'free', label: 'Free/Object', icon: 'OBJ', badge: actionSummary.free_object.available ? 'Available' : 'Used', status: actionSummary.free_object.available ? 'available' : 'used' }
                ];

                const getCategoryItems = (key) => {
                  // Prefer tokenActions, but always include a fallback to standardActions (per spec)
                  let base = tokenActions.length > 0 || familiarActions.length > 0
                    ? [...tokenActions, ...familiarActions]
                    : standardActions;
                  base = base.filter(a => !shouldHideActionForUI(a));
                  if (isInMultiattackMode && multiattackSequence) {
                    const currentAction = multiattackSequence.componentActions[multiattackSequence.currentIndex];
                    base = currentAction ? [currentAction] : [];
                  }

                  // @date_of_change 2025-11-09 - Lint cleanup: removed unused isActionSpeedSpell

                  // Normalize items to UI categories using ActionEconomy mapping
                  const normalizeForUi = (items) => dedupeActionEntries(items.map((a) => {
                    const name = typeof a.name === 'string' ? a.name : (a.name?.name || a.name?.toString() || 'Unknown Action');
                    const uiType = resolveUiTypeForAction({ ...a, name });
                    return { ...a, name, uiType };
                  }));

                  const normalized = normalizeForUi(base);
                  const normalizedStandard = normalizeForUi(standardActions);
                  const offHandAction = buildOffHandAttackAction(currentCombatant, tokenActions);

                  const only = (types) => normalized.filter((entry) => types.includes(entry.uiType));

                  switch (key) {
                    case 'action': {
                      // Hide generic placeholder 'Attack' (it has no handler); keep only concrete actions and generic non-attack actions
                      const mainActions = only(['attack', 'action']).filter((entry) => !isGenericAttackPlaceholder(entry));
                      const std = normalizedStandard.filter((entry) => entry.type === 'action' && !isGenericAttackPlaceholder(entry));
                      return dedupeActionEntries([...mainActions, ...std]);
                    }
                    case 'bonus': {
                      const baseBonus = only(['bonus']);
                      const conditionalBonus = [
                        canCreatureBonusDash(currentCombatant) ? buildConditionalStandardAction('bonus-action-dash') : null,
                        hasCunningAction(currentCombatant) ? buildConditionalStandardAction('bonus-action-disengage') : null,
                        hasCunningAction(currentCombatant) ? buildConditionalStandardAction('bonus-action-hide') : null,
                        canTwoWeaponFight(currentCombatant, tokenActions) ? offHandAction : null
                      ].filter(Boolean);
                      // Gate standard bonus actions by class/equipment
                      const stdBonus = normalizedStandard
                        .filter(s => s.type === 'bonus')
                        .flatMap(s => {
                          if (s.name === 'Second Wind') return isFighter(currentCombatant) ? [s] : [];
                          if (s.name === 'Off-hand Attack') return canTwoWeaponFight(currentCombatant, tokenActions) && offHandAction ? [offHandAction] : [];
                          return [s];
                        });
                      return dedupeActionEntries([...baseBonus, ...conditionalBonus, ...stdBonus]);
                    }
                    case 'move':
                      return [];
                    case 'reaction':
                      return only(['reaction']);
                    case 'free':
                      return dedupeActionEntries([
                        ...only(['free', 'object']),
                        ...normalizedStandard.filter(s => s.type === 'free' || s.type === 'object')
                      ]);
                    default:
                      return [];
                  }
                };

                const onToggle = (key) => {
                  console.log('[ActionTracker] Toggle category', { key, prev: expandedKey });
                  setExpandedKey(prev => (prev === key ? null : key));
                };

                const renderTray = (key) => {
                  const items = getCategoryItems(key);
                  if (!items || items.length === 0) return null;
                  const renderActionButton = (action, index, keyPrefix = key) => {
                    if (typeof action.name !== 'string') {
                      action.name = action.name?.name || action.name?.toString() || 'Unknown Action';
                    }
                    const actionType = resolveActionEconomyTypeForAction(action, key);
                    const movementCost = (() => {
                      if (action.name && action.name.toLowerCase().includes('move')) {
                        const m = String(action.name).match(/(\d+)\s*ft/i);
                        if (m && Number.isFinite(parseInt(m[1], 10))) return parseInt(m[1], 10);
                        return 30; // default move chunk
                      }
                      return 0;
                    })();

                    const isViewingOutOfTurn = Boolean(
                      combatStarted
                      && activeCombatant?.id
                      && currentCombatant?.id
                      && activeCombatant.id !== currentCombatant.id
                    );
                    let disabledReason = '';
                    let canUse = true;
                    if (!isInMultiattackMode) {
                      canUse = currentCombatant ? ActionEconomyManager.canUseAction(
                        currentCombatant.id,
                        actionType,
                        { movementCost, speed }
                      ) : false;
                      if (!canUse) {
                        disabledReason = ActionEconomyManager.getUnavailableReason(
                          actionType,
                          ActionEconomyManager.getCombatantState(currentCombatant.id),
                          movementCost,
                          speed
                        );
                      }
                    }
                    if (gmOverrideMode) {
                      canUse = true;
                      disabledReason = '';
                    }
                    if (isViewingOutOfTurn && !gmOverrideMode) {
                      canUse = false;
                      disabledReason = `Viewing ${currentCombatant.name || 'this token'}; it is ${activeCombatant?.name || 'another combatant'}'s turn.`;
                    }
                    // Structured prerequisites and malformed mechanics remain visible.
                    // Table chrome / RightPanel reference; date_of_change: 2026-09-05.
                    if (action.readinessError || (action.unavailableReason && !gmOverrideMode)) {
                      canUse = false;
                      disabledReason = action.readinessError || action.unavailableReason;
                    }
                    const offHandPrerequisite = getOffHandPrerequisite(currentCombatant.id, action, tokenActions);
                    if (!offHandPrerequisite.met && !gmOverrideMode) {
                      canUse = false;
                      disabledReason = offHandPrerequisite.reason;
                    }

                    const actionKey = buildActionListKey(action);
                    const isSearchAction = action.standardActionKey === 'search';
                    const isSearchDeclarationOpen = isSearchAction && searchDeclaration?.actionKey === actionKey;
                    const selectedKeenTraits = keenSenseEvidence.traits.filter((trait) => (
                      searchDeclaration?.skill === 'perception'
                      && trait.mechanics.senses.includes(searchDeclaration.sense)
                    ));
                    const canExecuteSearch = Boolean(
                      searchDeclaration?.skill === 'investigation'
                      || (
                        searchDeclaration?.skill === 'perception'
                        && SEARCH_SENSE_OPTIONS.some((option) => option.value === searchDeclaration.sense)
                      )
                    );

                    return (
                      <React.Fragment key={`${keyPrefix}-${action.standardActionKey || action.name}-${index}`}>
                        <button
                          className={`action-button ${action.type} ${!canUse ? 'disabled' : ''} ${isInMultiattackMode ? 'multiattack-component' : ''}`}
                          disabled={!canUse}
                          onClick={() => handleActionButtonClicked(action)}
                          title={!canUse ? `Cannot use: ${disabledReason}` : (action.desc || action.description || action.ruleReference)}
                          aria-expanded={isSearchAction ? isSearchDeclarationOpen : undefined}
                        >
                          {action.displayName || action.name}
                          {!canUse && <span className="creature-action-reason">{disabledReason}</span>}
                          {isInMultiattackMode && multiattackSequence && (
                            <span className="multiattack-indicator">
                              ({multiattackSequence.currentIndex + 1}/{multiattackSequence.componentActions.length})
                            </span>
                          )}
                        </button>
                        {isSearchDeclarationOpen && (
                          <section
                            className="search-action-declaration"
                            aria-labelledby="search-action-declaration-title"
                            data-testid="search-action-declaration"
                          >
                            <div className="search-action-declaration__heading">
                              <strong id="search-action-declaration-title">Declare Search context</strong>
                              <span>No inferred skill or sense</span>
                            </div>
                            <label>
                              <span>Check</span>
                              <select
                                aria-label="Search check"
                                value={searchDeclaration.skill}
                                onChange={(event) => setSearchDeclaration((current) => ({
                                  ...current,
                                  skill: event.target.value,
                                  sense: ''
                                }))}
                              >
                                <option value="">Choose a check</option>
                                <option value="perception">Wisdom (Perception)</option>
                                <option value="investigation">Intelligence (Investigation)</option>
                              </select>
                            </label>
                            {searchDeclaration.skill === 'perception' && (
                              <label>
                                <span>Perception context</span>
                                <select
                                  aria-label="Perception sense context"
                                  value={searchDeclaration.sense}
                                  onChange={(event) => setSearchDeclaration((current) => ({
                                    ...current,
                                    sense: event.target.value
                                  }))}
                                >
                                  <option value="">Choose the relied-on sense</option>
                                  {SEARCH_SENSE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                </select>
                              </label>
                            )}
                            {searchDeclaration.skill === 'perception' && keenSenseEvidence.traits.length > 0 && (
                              <div className="search-action-declaration__evidence" aria-live="polite">
                                <strong>Keen sense evidence</strong>
                                {keenSenseEvidence.traits.map((trait) => (
                                  <span key={trait.id}>{trait.name}: {trait.mechanics.senses.join(' or ')}</span>
                                ))}
                                <span>
                                  {selectedKeenTraits.length > 0
                                    ? `Advantage applies: ${selectedKeenTraits.map((trait) => trait.name).join(', ')}.`
                                    : 'No Keen advantage applies to the selected context.'}
                                </span>
                                {keenSenseEvidence.passivePerception != null && (
                                  <span>
                                    Base passive Perception: {keenSenseEvidence.passivePerception}. Contextual passive Perception: {keenSenseEvidence.passivePerception + 5} when a named Keen sense grants advantage; disadvantage can cancel it.
                                  </span>
                                )}
                                <span>Passive observation must name its relied-on sense; VCS does not infer the trigger.</span>
                              </div>
                            )}
                            {searchDeclaration.skill === 'investigation' && keenSenseEvidence.traits.length > 0 && (
                              <p className="search-action-declaration__note">Keen senses modify Wisdom (Perception), not Intelligence (Investigation).</p>
                            )}
                            <div className="search-action-declaration__actions">
                              <button type="button" onClick={() => setSearchDeclaration(null)}>Cancel</button>
                              <button type="button" disabled={!canExecuteSearch} onClick={executeDeclaredSearch}>Execute Search</button>
                            </div>
                          </section>
                        )}
                      </React.Fragment>
                    );
                  };

                  if (items.length < LONG_ACTION_TRAY_THRESHOLD) {
                    return (
                      <div className="tray" id={`tray-${key}`}>
                        {items.map((action, index) => renderActionButton(action, index))}
                      </div>
                    );
                  }

                  const groups = groupActionItems(items);
                  return (
                    <div className="tray tray-grouped" id={`tray-${key}`}>
                      {groups.map((group, groupIndex) => (
                        <details className="action-subgroup" key={`${key}-${group.key}`} open={groups.length === 1 && group.items.length <= 4}>
                          <summary>
                            <span>{group.label}</span>
                            <span className="action-subgroup-count">{group.items.length}</span>
                          </summary>
                          <div className="action-subgroup-items">
                            {group.items.map((action, index) => renderActionButton(action, `${groupIndex}-${index}`, `${key}-${group.key}`))}
                          </div>
                        </details>
                      ))}
                    </div>
                  );
                };

                return (
                  <div className="economy-stack">
                    {categories.map(cat => (
                      <div key={cat.key} className={`economy-row economy-${cat.key}`}>
                        {cat.interactive === false ? (
                          <div className="economy-button economy-status" data-status={cat.status} aria-label={`${cat.label}: ${cat.badge}`}>
                            <span className="economy-icon">{cat.icon}</span>
                            <span className="economy-label">{cat.label}</span>
                            <span className="economy-badge">{cat.badge}</span>
                          </div>
                        ) : (
                          <button type="button" className="economy-button" data-status={cat.status} onClick={() => onToggle(cat.key)} aria-expanded={expandedKey === cat.key} aria-controls={`tray-${cat.key}`}>
                            <span className="economy-icon">{cat.icon}</span>
                            <span className="economy-label">{cat.label}</span>
                            <span className="economy-badge">{cat.badge}</span>
                          </button>
                        )}
                        {expandedKey === cat.key && renderTray(cat.key)}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
        </>
      )}

      {/* Current Turn Actions */}
      {currentTurnActions.length > 0 && (
        <div className="current-turn-actions">
          <h5>This Turn:</h5>
          <div className="action-list">
            {currentTurnActions.map((action) => {
              const ActionIcon = actionTypes[action.type]?.Icon;
              return (
              <div key={action.id} className={`action-item action-${action.type}`}>
                <div className="action-main">
                  {ActionIcon && <span className="action-icon" aria-hidden="true"><ActionIcon size={13} /></span>}
                  <span className="action-name">{action.name}</span>
                  <span className="action-time">{action.timestamp}</span>
                  {action.details.movementCost > 0 && (
                    <span className="movement-cost"> ({action.details.movementCost}ft)</span>
                  )}
                </div>
                {action.details.hitRoll && (
                  <div className="action-details">
                    <div className={`hit-result ${action.details.hit ? 'hit' : 'miss'}`}>
                      {/* @date_of_change 2025-08-27 Show actual attack bonus number if available */}
                      <span className="result-label">Attack</span> {action.details.hitRoll} + {(action.details.attackBonus ?? 0)} = {action.details.totalHit}
                      {action.details.critical && ' (CRIT!)'}
                    </div>
                    {action.details.hit && action.details.damageRoll && (
                      <div className="damage-result">
                        <span className="result-label">Damage</span> {action.details.damageRoll}
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Action Summary */}
      <div className="action-summary">
        <div className="action-counts">
          {Object.entries(actionTypes).map(([type, config]) => {
            const count = currentTurnActions.filter(a => a.type === type).length;
            return count > 0 ? (
              <span key={type} className={`action-count action-${type}`}>
                <config.Icon size={12} aria-hidden="true" />
                <span>{config.name}: {count}</span>
              </span>
            ) : null;
          })}
        </div>
      </div>
    </div>
  );
};

export default ActionTracker;
